import { jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useMemo, useCallback, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { MotionConfig, AnimatePresence, motion } from "framer-motion";
import { easeQuadOut } from "d3-ease";
import { tabbable } from "tabbable";
import { Popover } from "@reach/popover";
import { composeEventHandlers } from "@reach/utils";
import { tabbableNext } from "../../utils/tabbable-next.es.js";
import { usePopoverPosition } from "../../hooks/use-popover-position.es.js";
const propTypes = {
  animationAxis: PropTypes.oneOf(["both", "x", "y"]),
  as: PropTypes.elementType,
  children: PropTypes.node.isRequired,
  justify: PropTypes.oneOf(["start", "center", "end", "stretch"]).isRequired,
  offset: PropTypes.number,
  onKeyDown: PropTypes.func,
  open: PropTypes.bool,
  placement: PropTypes.oneOf(["top", "bottom", "left", "right"]).isRequired,
  preventOverflow: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  targetRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired
};
const defaultProps = {
  animationAxis: void 0,
  as: void 0,
  offset: void 0,
  onKeyDown: void 0,
  open: false,
  preventOverflow: true,
  stopPropagation: false
};
const AnimatedPopover = forwardRef(
  ({
    animationAxis,
    as,
    children,
    justify,
    offset,
    onKeyDown,
    open,
    placement,
    preventOverflow,
    stopPropagation,
    targetRef,
    ...restProps
  }, forwardedRef) => {
    const ref = useRef();
    const transformName = useMemo(() => {
      if (animationAxis === "x") {
        return "scaleX";
      }
      if (animationAxis === "y") {
        return "scaleY";
      }
      if (justify === "stretch" && (placement === "left" || placement === "right")) {
        return "scaleX";
      }
      if (justify === "stretch" && (placement === "top" || placement === "bottom")) {
        return "scaleY";
      }
      return "scale";
    }, [animationAxis, justify, placement]);
    const position = usePopoverPosition({
      placement,
      justify,
      offset,
      preventOverflow
    });
    const handleClick = useCallback(
      (event) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
      },
      [stopPropagation]
    );
    const handleKeyDown = useMemo(
      () => composeEventHandlers(onKeyDown, (event) => {
        if (event.key === "Tab") {
          const tabbableNodes = tabbable(ref.current);
          const index = tabbableNodes.indexOf(event.target);
          if (event.shiftKey && (index === 0 || tabbableNodes.length === 0) && targetRef.current) {
            event.preventDefault();
            targetRef.current.focus();
          } else if (!event.shiftKey && index === tabbableNodes.length - 1) {
            const nextNode = tabbableNext(targetRef.current);
            if (nextNode) {
              event.preventDefault();
              nextNode.focus();
            }
          }
        }
      }),
      [onKeyDown, targetRef]
    );
    useImperativeHandle(forwardedRef, () => ref.current);
    return /* @__PURE__ */ jsx(
      Popover,
      {
        ...restProps,
        ref,
        as,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        position,
        targetRef,
        children: /* @__PURE__ */ jsx(MotionConfig, { transition: { ease: easeQuadOut, duration: 0.1 }, children: /* @__PURE__ */ jsx(AnimatePresence, { initial: false, children: open && /* @__PURE__ */ jsx(
          motion.div,
          {
            style: { transformOrigin: "inherit" },
            initial: { opacity: 0, transform: `${transformName}(0.8)` },
            animate: { opacity: 1, transform: `${transformName}(1)` },
            exit: { opacity: 0, transform: `${transformName}(0.8)` },
            children
          }
        ) }) })
      }
    );
  }
);
AnimatedPopover.propTypes = propTypes;
AnimatedPopover.defaultProps = defaultProps;
export {
  AnimatedPopover
};
